var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'string-format';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { decodeCreateNodeUpgradeResponse, decodeGetSystemNodeUpgradeResponse } from '../../../types/lighthouse/decoders/system/node-upgrade';
import { decodeReadNodeUpgradeResponse, decodeDeleteNodeUpgradeResponse } from '../../../types/lighthouse/decoders/system/node-upgrade/upgrade-id';
import { decodeReadNodeUpgradeNodeResponse } from '../../../types/lighthouse/decoders/system/node-upgrade/upgrade-id/nodes';
import { useLighthouseFetch } from '@/hooks/use-lighthouse-fetch';
export var NODE_UPGRADE_PATH = '/system/node_upgrade';
export var UPGRADE_NODES = '/nodes';
export function formatNodeUpgradePath(upgradeId) {
    if (upgradeId === void 0) { upgradeId = ':upgradeId'; }
    return "".concat(NODE_UPGRADE_PATH, "/").concat(upgradeId);
}
export function formatNodeUpgradeNodesPath(upgradeId) {
    if (upgradeId === void 0) { upgradeId = ':upgradeId'; }
    return "".concat(NODE_UPGRADE_PATH, "/").concat(upgradeId).concat(UPGRADE_NODES);
}
export function useNodeUpgrades(queryParams) {
    var _this = this;
    if (queryParams === void 0) { queryParams = {}; }
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var t = useTranslation().t;
    var fetchNodeUpgrade = useCallback(function (context) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(NODE_UPGRADE_PATH, __assign({ queryParams: queryParams }, context))];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeGetSystemNodeUpgradeResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    handleCommonStatusCodes(data, status, t('api.node_upgrade.get'));
                    return [2 /*return*/, status === 200 ? data : null];
            }
        });
    }); }, [executeFetch, decodeResponse, handleCommonStatusCodes, queryParams, t]);
    return useQuery({
        queryKey: [NODE_UPGRADE_PATH, queryParams],
        queryFn: fetchNodeUpgrade,
    });
}
export function useNodeUpgrade(upgradeId, _a) {
    var _this = this;
    var _b = _a === void 0 ? {} : _a, notFoundCallback = _b.notFoundCallback;
    var _c = useLighthouseFetch(), executeFetch = _c.executeFetch, decodeResponse = _c.decodeResponse, handleCommonStatusCodes = _c.handleCommonStatusCodes;
    var t = useTranslation().t;
    var fetchNodeUpgrade = useCallback(function (context) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status, translations;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(formatNodeUpgradePath(upgradeId), __assign({}, context))];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeReadNodeUpgradeResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    translations = {
                        error: { message: format(t('api.node_upgrade.get.error.message'), upgradeId) },
                        forbidden: { message: format(t('api.node_upgrade.get.forbidden.message'), upgradeId) },
                        not_found: { message: format(t('api.node_upgrade.get.not_found.message'), upgradeId) },
                    };
                    handleCommonStatusCodes(data, status, translations);
                    if ((status === 403 || status === 404) && notFoundCallback) {
                        notFoundCallback(status);
                    }
                    return [2 /*return*/, status === 200 ? data.node_upgrade : null];
            }
        });
    }); }, [decodeResponse, executeFetch, handleCommonStatusCodes, notFoundCallback, t, upgradeId]);
    return useQuery({
        queryKey: [NODE_UPGRADE_PATH, upgradeId],
        queryFn: fetchNodeUpgrade,
    });
}
export function useCreateNodeUpgrade() {
    var _this = this;
    var t = useTranslation().t;
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var queryClient = useQueryClient();
    return useCallback(function (nodeUpgrade) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(NODE_UPGRADE_PATH, { method: 'POST', body: JSON.stringify({ node_upgrade: nodeUpgrade }) })];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeCreateNodeUpgradeResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    handleCommonStatusCodes(data, status, t('api.node_upgrades.post'));
                    if (status === 201) {
                        queryClient
                            .invalidateQueries({
                            queryKey: [NODE_UPGRADE_PATH],
                        })
                            .catch(console.error);
                        queryClient.setQueryData([NODE_UPGRADE_PATH, data.node_upgrade.id], data.node_upgrade);
                        return [2 /*return*/, data.node_upgrade];
                    }
                    return [2 /*return*/, null];
            }
        });
    }); }, [decodeResponse, executeFetch, handleCommonStatusCodes, queryClient, t]);
}
export function useDeleteNodeUpgrade() {
    var _this = this;
    var t = useTranslation().t;
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var queryClient = useQueryClient();
    return useCallback(function (upgradeId) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status, translations;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(formatNodeUpgradePath(upgradeId), { method: 'DELETE' })];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeDeleteNodeUpgradeResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    translations = {
                        error: { message: format(t('api.node_upgrade.delete.error.message'), upgradeId) },
                        forbidden: { message: format(t('api.node_upgrade.delete.forbidden.message'), upgradeId) },
                        not_found: { message: format(t('api.node_upgrade.delete.not_found.message'), upgradeId) },
                    };
                    if (status === 406) {
                        translations.not_accepted = { message: format(t('api.node_upgrade.delete.not_accepted.message'), data.error[0].text, upgradeId) };
                    }
                    handleCommonStatusCodes(data, status, translations);
                    if (status === 200 || status === 202) {
                        queryClient
                            .invalidateQueries({
                            queryKey: [NODE_UPGRADE_PATH, upgradeId],
                        })
                            .catch(console.error);
                        return [2 /*return*/, true];
                    }
                    return [2 /*return*/, false];
            }
        });
    }); }, [decodeResponse, executeFetch, handleCommonStatusCodes, queryClient, t]);
}
export function useNodeUpgradeNodes(upgradeId, queryParams) {
    var _this = this;
    if (queryParams === void 0) { queryParams = {}; }
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var t = useTranslation().t;
    var fetchNodeUpgradeNodes = useCallback(function (context) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status, translations;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(formatNodeUpgradeNodesPath(upgradeId), __assign({ queryParams: queryParams }, context))];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeReadNodeUpgradeNodeResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    translations = {
                        error: { message: format(t('api.node_upgrade_nodes.get.error.message'), upgradeId) },
                        forbidden: { message: format(t('api.node_upgrade_nodes.get.forbidden.message'), upgradeId) },
                        not_found: { message: format(t('api.node_upgrade_nodes.get.not_found.message'), upgradeId) },
                    };
                    handleCommonStatusCodes(data, status, translations);
                    return [2 /*return*/, status === 200 ? data : null];
            }
        });
    }); }, [decodeResponse, executeFetch, handleCommonStatusCodes, queryParams, t, upgradeId]);
    return useQuery({
        queryKey: [NODE_UPGRADE_PATH, UPGRADE_NODES, upgradeId, queryParams],
        queryFn: fetchNodeUpgradeNodes,
    });
}
